import React from 'react';
import Icon from 'react-mdl/lib/Icon';
import { timeDiff, secondsToDuration, moneyFormat } from '../utils.js';
import TrainRestrictions from './TrainRestrictions';
import { t } from '../lang/trans.js';
import sum from 'lodash/fp/sum';
import { legPrice, routePrice } from '../price.js';
import Button from 'react-mdl/lib/Button';
import { Card, CardTitle, CardText, CardActions } from 'react-mdl/lib/Card';
import ReactHighcharts from 'react-highcharts';
import { map } from 'lodash';

const halfPadding = { paddingLeft: '12px', paddingRight: '12px'}
const renderLeg = leg => {
  const distance = sum(leg.segments.map(s => s.distance));
  const price = legPrice(leg);
  const rows = [
    <tr>
      <td colSpan={2} style={{textAlign: 'center'}}>
        <h4 style={{ whiteSpace: 'pre-wrap' }}>
          <strong>{leg.train.type} {leg.train.number}</strong>
          {' '}<TrainRestrictions train={leg.train} />
          {leg.train.operator !== 'CFR' ?
            <span>
              <br/>
              {{'RGT': 'RegioTrans', 'TFC': 'TransFeroviar', 'SFT': 'Softrans'}[leg.train.operator]}
            </span>
          : ''}
        </h4>
      </td>
    </tr>,
    price ?
      <tr>
        <td colSpan="2" style={{textAlign: 'center'}}>
          {distance / 1000} km &mdash; {moneyFormat(price)} lei
        </td>
      </tr>
    : ''
  ];

  return rows.concat(
    leg.segments.map((segment, idx) =>
      <tr key={`${idx} ${leg.train.number}`}>
        <td colSpan={2} style={halfPadding} className="mdl-data-table__cell--non-numeric">
          <div style={{ float: 'left' }}>
            <strong>{segment.leavesAt}</strong>{' '}
            {segment.from}
          </div>
          <div style={{ float: 'right' }}>
            {segment.to}{' '}
            <strong>{segment.arrivesAt}</strong>
          </div>
        </td>
      </tr>
    )
  );
}

const renderLegs = legs => {
  return [].concat.apply([], legs.map((leg, idx) => {
    const legRows = renderLeg(leg)
    if(idx < legs.length - 1) {
      legRows.push(
        <tr key={`waitTime ${leg.train.number}`}>
          <th colSpan={2} className="mdl-data-table__cell--non-numeric" style={{ textAlign: 'center' }}>
            {t('wait for')}
            <strong>
              {' '}<Icon name="access_time" style={{ fontSize: '12px' }} />
              {' '}{secondsToDuration(timeDiff(leg.arrivesAt, legs[idx + 1].leavesAt))}
            </strong>
            ,{' '}{t('and then')}:
          </th>
        </tr>
      );
    }

    return legRows;
  }));
}

export default class Train extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  expand(fetchOnly) {
    if(fetchOnly !== true) {
      this.setState({ expanded: !this.state.expanded });
    }

    if(!this.state.delayDetails && this.props.firebase) {
      this.props.firebase.ref(`/delayDetails/${this.props.train.legs[0].train.number}`).once('value', node => {
        this.setState({
          delayDetails: node.val() || undefined
        });
      });
    }
  }

  renderDelays() {
    if(!this.state.delayDetails || this.props.train.legs.length > 1) {
      return null;
    }
    const runs = this.state.delayDetails.runs;
    let stations = [];
    map(this.state.delayDetails.runs, run => {
      if(run.length > stations.length) {
        stations = run.map(([k]) => k);
      }
    });

    const series = [];
    let run;

    let idx = 0;

    for(let date in runs) {
      run = runs[date];
      series.push({
        type: 'line',
        name: date,
        marker: {
          enabled: false
        },
        data: run,
        color: '#444',
        lineWidth: 1
      });

      idx ++;
    }


    const config = {
      title: { text: t('Delays last month') },
      series: series,
      xAxis: {
        categories: stations
      },
      yAxis: {
        labels: {
          format: '{value} min'
        },
        title: {
          text: t('delay')
        }
      },
      legend: {
        enabled: false
      }
    };
    return <ReactHighcharts config={config} />
  }

  render() {
    const { train, showStations, isFastest, forceOpen } = this.props;
    if(forceOpen) {
      setTimeout(() => {
        this.expand(true); // force fetching from firebase
      }, 50);
    }
    let duration;

    if(typeof train.legs !== 'undefined') {
      duration = secondsToDuration(timeDiff(train.legs[0].leavesAt, train.legs[train.legs.length - 1].arrivesAt));
    } else {
      duration = secondsToDuration(timeDiff(train.leavesAt, train.arrivesAt));
    }
    const price = routePrice(train);
    const dom = (
      <Card style={{ marginTop: '1px', width: '100%' }} shadow={0} className={`Train ${forceOpen ? '' : 'expandable'}`}>
        <CardTitle
          style={{ backgroundColor: isFastest ? '#eee' : 'white' }}
          onClick={this.expand.bind(this)}
        >
          <span>
            <strong>
              <Icon name={train.legs[0].train.type === 'R' ? 'train' : 'flash_on'} style={{ fontSize: '13px' }} />
              {train.legs.map(leg => `${leg.train.type} ${leg.train.number}`).join(' + ')}
            </strong>

            {' '}{t('time from')}
            <strong>{' '}{train.leavesAt}</strong>

            {' '}{t('time to')}
            <strong>{' '}{train.arrivesAt}</strong>
            {price ?
              <span>{', '}{t('costs')} {moneyFormat(price)} {t('lei')}</span>
            : ''}
            <br/>
            {isFastest ? <Icon name="stars" style={{ fontSize: '12px' }} /> : ''}
            {showStations ?
              <span>
                {t('time from')} {train.from} {t('time to')} {train.to}
                <br/>
              </span>
            : ''}
            <small>
              <strong>{duration}</strong>
              {train.legs.length > 1 ?
                <span> via {train.legs.slice(0, -1).map(leg => leg.to).join(', ')}</span>
              : ''}
              {isFastest ?
                <span> &mdash; {t('fastest')}!</span>
              : ''}
            </small>
          </span>
          {forceOpen ? null :
            <div style={{ position: 'absolute', right: '20px' }}>
              <Icon name={this.state.expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'} />
            </div>
          }
        </CardTitle>
        {!this.state.expanded && !forceOpen ? null :
          <CardText style={{ width: 'auto' }}>
            <table style={{ width: '100%' }} className="mdl-data-table mdl-js-data-table">
              <tbody>
                {renderLegs(train.legs)}
              </tbody>
            </table>

            {this.renderDelays()}
          </CardText>
        }
        {!this.state.expanded && !forceOpen ? null :
          <CardActions>
            <a href={'mailto:gabi@purcaru.com?subject=Acceleratul - Eroare tren ' + train.legs.map(leg => leg.train.type + leg.train.number).join(', ')}>
              <Button raised accent>
                <Icon name="error_outline" />
                {t('report error')}
              </Button>
            </a>
          </CardActions>
        }
      </Card>
    );

    return dom;
  }
}



/** WEBPACK FOOTER **
 ** ./src/components/Train.js
 **/